import { ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/** Sub-pixel jitter in a measured height that must not count as a change. */
const HEIGHT_EPSILON = 0.5;

/** Slack when comparing a scroll position against the end of its range. */
const END_TOLERANCE_PX = 1;

/**
 * Virtual-scroll strategy for rows whose height comes from their content.
 *
 * The fixed-size strategy maps a scroll offset onto a row index through one `itemSize`.
 * Table rows wrap by design — text clamps at three lines, list cells stack up to three
 * rows of pills — so no single size fits them all, and the mapping drifts by the
 * accumulated difference: enough for the last row to come to rest below the viewport with
 * no scroll range left to reach it.
 *
 * This keeps a height per row instead, measured off the DOM once the row has rendered,
 * and derives both the total content size and every rendered row's offset from that one
 * array. Rows never rendered are counted at the mean of the measured ones, so the total is
 * an estimate until the whole list has been visited. That estimate only moves the
 * scrollbar, never the rows: offsets and total size come from the same array, so the last
 * row's bottom edge always lands exactly at the end of the scroll range.
 *
 * Refining the estimate does change the length of the range, though, which would leave a
 * viewer parked at the very end a little short of it. Such a viewer is therefore carried
 * to the new end (see `restoreEndAnchor`), which is also what makes reaching the end in a
 * single drag land on the last row.
 *
 * Assumes each row renders as exactly one element — the direct children of CDK's content
 * wrapper are read as the rendered rows, in range order.
 */
export class MeasuredRowVirtualScrollStrategy implements VirtualScrollStrategy {
  private readonly scrolledIndexChangeSubject = new Subject<number>();

  /** @docs-private Implemented as part of VirtualScrollStrategy. */
  readonly scrolledIndexChange: Observable<number> =
    this.scrolledIndexChangeSubject.pipe(distinctUntilChanged());

  private viewport: CdkVirtualScrollViewport | null = null;

  /** Measured height per row index; `undefined` until that row has been rendered. */
  private heights: (number | undefined)[] = [];
  /** `offsets[i]` is the top of row `i` within the content; length is row count + 1. */
  private offsets: number[] = [0];
  private offsetsStale = true;

  private measuredCount = 0;
  private measuredTotal = 0;
  /** Row height for unmeasured rows, until measurements of our own replace it. */
  private estimateHint: number;

  /** Set while the viewer sits at the end of the scroll range, with this row count. */
  private anchoredToEnd = false;
  private anchorDataLength = 0;

  /** Notices when the content size this strategy asked for has reached the DOM. */
  private contentSizeObserver?: ResizeObserver;

  /**
   * @param estimatedRowHeightPx Row height assumed before anything has been measured.
   * @param minBufferPx How close the rendered rows may come to the viewport edge.
   * @param maxBufferPx How far past the viewport to render when topping the buffer up.
   */
  constructor(
    estimatedRowHeightPx: number,
    private readonly minBufferPx: number,
    private readonly maxBufferPx: number
  ) {
    this.estimateHint = estimatedRowHeightPx;
  }

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.invalidateMeasurements();

    // The viewport element's height is the content size this strategy sets, so its resizes
    // are the signal that a new total has been applied — the point at which a viewer
    // parked at the end has to be carried along.
    this.contentSizeObserver = new ResizeObserver(() => this.restoreEndAnchor());
    this.contentSizeObserver.observe(viewport.elementRef.nativeElement);
  }

  detach(): void {
    this.contentSizeObserver?.disconnect();
    this.contentSizeObserver = undefined;
    this.scrolledIndexChangeSubject.complete();
    this.viewport = null;
  }

  /**
   * Drops every row measurement and takes them again, for the cases where rows change
   * height without a render to report it: different data behind the same indices, or a
   * container width that rewraps the cells.
   */
  private invalidateMeasurements(): void {
    if (this.measuredCount > 0) {
      this.estimateHint = this.measuredTotal / this.measuredCount;
    }
    this.heights = new Array(this.viewport?.getDataLength() ?? 0);
    this.measuredCount = 0;
    this.measuredTotal = 0;
    this.offsetsStale = true;
    this.anchoredToEnd = false;
    this.measureRenderedRows();
    this.updateRenderedRange();
  }

  onContentScrolled(): void {
    // Rows already measured stay measured, so this normally reads nothing at all: it picks
    // up rows the previous pass rendered but could not yet measure.
    this.measureRenderedRows();
    this.updateRenderedRange();
  }

  onDataLengthChanged(): void {
    // Rows keep neither their index nor their height across a data change, and this is
    // also where `checkViewportSize` lands, which is how a resized container gets its
    // rewrapped rows re-measured.
    this.invalidateMeasurements();
  }

  onContentRendered(): void {
    if (this.measureRenderedRows()) {
      this.updateRenderedRange();
    }
  }

  onRenderedOffsetChanged(): void {}

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    const viewport = this.viewport;
    if (!viewport) return;

    if (this.offsetsStale) this.rebuildOffsets();
    const clamped = Math.max(0, Math.min(index, this.offsets.length - 1));
    // `scrollToOffset` writes the scroller's own scroll position, which counts from above
    // the viewport (the table header sits in between), while offsets count from the first
    // row. `measureViewportOffset` is the difference.
    viewport.scrollToOffset(
      this.offsets[clamped] + viewport.measureViewportOffset(),
      behavior
    );
  }

  /**
   * Reads the height of every rendered row that is unmeasured or has changed height.
   * Returns whether any height moved, i.e. whether the offsets have to be rebuilt.
   */
  private measureRenderedRows(): boolean {
    const viewport = this.viewport;
    if (!viewport) return false;

    const rows = this.renderedRowElements();
    if (!rows) return false;

    const { start } = viewport.getRenderedRange();
    let changed = false;

    for (let i = 0; i < rows.length; i++) {
      const height = rows[i].getBoundingClientRect().height;
      // A row with no height has not been laid out yet; leave it for the next pass rather
      // than recording a zero that would collapse the offsets.
      if (height <= 0) continue;

      const index = start + i;
      const previous = this.heights[index];
      if (previous !== undefined && Math.abs(previous - height) <= HEIGHT_EPSILON) continue;

      if (previous === undefined) {
        this.measuredCount++;
      } else {
        this.measuredTotal -= previous;
      }
      this.measuredTotal += height;
      this.heights[index] = height;
      changed = true;
    }

    if (changed) this.offsetsStale = true;
    return changed;
  }

  /**
   * The rendered rows, in range order, or `null` when the DOM does not line up with the
   * rendered range — mid-update, when measuring against the wrong indices would be worse
   * than not measuring at all.
   */
  private renderedRowElements(): HTMLElement[] | null {
    const viewport = this.viewport;
    if (!viewport) return null;

    const wrapper = viewport.elementRef.nativeElement.querySelector(
      '.cdk-virtual-scroll-content-wrapper'
    );
    if (!wrapper) return null;

    const range = viewport.getRenderedRange();
    const children = wrapper.children;
    if (children.length !== range.end - range.start) return null;

    return Array.from(children) as HTMLElement[];
  }

  private rebuildOffsets(): void {
    const length = this.viewport?.getDataLength() ?? 0;
    const estimate = this.rowHeightEstimate();
    const offsets = new Array<number>(length + 1);

    offsets[0] = 0;
    for (let i = 0; i < length; i++) {
      offsets[i + 1] = offsets[i] + (this.heights[i] ?? estimate);
    }

    this.offsets = offsets;
    this.offsetsStale = false;
  }

  private rowHeightEstimate(): number {
    return this.measuredCount > 0
      ? this.measuredTotal / this.measuredCount
      : this.estimateHint;
  }

  /** Index of the row containing `offset`, clamped to the data. */
  private indexAtOffset(offset: number): number {
    const offsets = this.offsets;
    const length = offsets.length - 1;
    if (length <= 0) return 0;

    let low = 0;
    let high = length - 1;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if (offsets[mid] <= offset) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }
    return low;
  }

  private updateRenderedRange(): void {
    const viewport = this.viewport;
    if (!viewport) return;

    if (this.offsetsStale) this.rebuildOffsets();

    const offsets = this.offsets;
    const length = offsets.length - 1;

    // Read where the viewer sits before publishing a new content size, so a position at
    // the end is recognised against the range they were actually looking at.
    this.anchoredToEnd = this.isScrolledToEnd();
    this.anchorDataLength = length;

    viewport.setTotalContentSize(offsets[length]);

    if (length === 0) {
      viewport.setRenderedRange({ start: 0, end: 0 });
      viewport.setRenderedContentOffset(0);
      this.scrolledIndexChangeSubject.next(0);
      return;
    }

    const viewportSize = viewport.getViewportSize();
    const scrollOffset = Math.max(0, viewport.measureScrollOffset());
    const viewportEnd = scrollOffset + viewportSize;

    const rendered = viewport.getRenderedRange();
    const range: ListRange = {
      start: Math.min(rendered.start, length),
      end: Math.min(rendered.end, length)
    };

    const needsRowsAbove =
      range.start > 0 && scrollOffset - offsets[range.start] < this.minBufferPx;
    const needsRowsBelow =
      range.end < length && offsets[range.end] - viewportEnd < this.minBufferPx;

    if (range.end <= range.start || needsRowsAbove || needsRowsBelow) {
      range.start = this.indexAtOffset(scrollOffset - this.maxBufferPx);
      range.end = Math.min(length, this.indexAtOffset(viewportEnd + this.maxBufferPx) + 1);
    }

    viewport.setRenderedRange(range);
    viewport.setRenderedContentOffset(offsets[range.start]);
    this.scrolledIndexChangeSubject.next(this.indexAtOffset(scrollOffset));
  }

  private scrollerElement(): HTMLElement | null {
    return this.viewport?.scrollable?.getElementRef().nativeElement ?? null;
  }

  private isScrolledToEnd(): boolean {
    const element = this.scrollerElement();
    if (!element) return false;

    const maxScrollTop = element.scrollHeight - element.clientHeight;
    // A table with nothing to scroll is not "at the end": treating it as such would send
    // it chasing the bottom as soon as its rows arrived.
    if (maxScrollTop <= END_TOLERANCE_PX) return false;

    return element.scrollTop >= maxScrollTop - END_TOLERANCE_PX;
  }

  /**
   * Keeps a viewer who was at the end of the scroll range there once a refined measurement
   * has moved that end. Only for a range that grew under the same rows — a different row
   * count is a new list, and pulling the viewer to the bottom of it would be its own bug.
   */
  private restoreEndAnchor(): void {
    if (!this.anchoredToEnd) return;
    if (this.viewport?.getDataLength() !== this.anchorDataLength) return;

    const element = this.scrollerElement();
    if (!element || this.isScrolledToEnd()) return;

    // Clamped by the browser; the scroll it fires runs the whole pass again, which settles
    // once the rows at the end have been measured.
    element.scrollTop = element.scrollHeight;
  }
}
